import { useState, useEffect, useMemo } from "react";
import { useOutletContext, Link } from "react-router-dom";
import {
  Plus,
  Thermometer,
  Pill,
  ClipboardList,
  StickyNote,
  Trash2,
  ChevronLeft,
  Activity,
  Heart,
  Droplet,
  Sparkles,
} from "lucide-react";
import { supabase } from "@/lib/supabase";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const SEVERITY_COLOR = {
  mild: "bg-emerald-100 text-emerald-700",
  moderate: "bg-amber-100 text-amber-700",
  severe: "bg-rose-100 text-rose-700",
};

export default function SickKidMode() {
  const { profiles } = useOutletContext();
  const [episodes, setEpisodes] = useState([]);
  const [activeEpisode, setActiveEpisode] = useState(null);
  const [openNew, setOpenNew] = useState(false);
  const [filter, setFilter] = useState("all");
  const [error, setError] = useState("");

  const loadEpisodes = async () => {
    let q = supabase
      .from("sick_episodes")
      .select("*")
      .order("start_date", { ascending: false });
    if (filter !== "all") q = q.eq("profile_id", filter);
    const { data, error: e } = await q;
    if (e) setError(e.message);
    else setEpisodes(data || []);
  };

  useEffect(() => {
    loadEpisodes();
    // eslint-disable-next-line
  }, [filter]);

  const noProfiles = !profiles || profiles.length === 0;

  if (activeEpisode) {
    return (
      <EpisodeDetail
        episode={activeEpisode}
        profile={profiles.find((p) => p.id === activeEpisode.profile_id)}
        onBack={() => {
          setActiveEpisode(null);
          loadEpisodes();
        }}
      />
    );
  }

  return (
    <div className="mx-auto max-w-6xl space-y-8" data-testid="sick-page">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <span className="text-xs font-semibold uppercase tracking-wider text-accent">
            Sick Kid Mode
          </span>
          <h1 className="mt-1 font-heading text-3xl font-extrabold tracking-tight">
            Illness Tracker
          </h1>
          <p className="mt-1 max-w-md text-sm text-muted-foreground">
            Track symptoms, fevers, doses, and notes when someone's not feeling well.
          </p>
        </div>
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
          {profiles?.length > 1 && (
            <Select value={filter} onValueChange={setFilter}>
              <SelectTrigger className="w-44 bg-card" data-testid="sick-filter-profile">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All profiles</SelectItem>
                {profiles.map((p) => (
                  <SelectItem key={p.id} value={p.id}>
                    {p.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
          <Dialog open={openNew} onOpenChange={setOpenNew}>
            <DialogTrigger asChild>
              <Button size="lg" disabled={noProfiles} data-testid="sick-new-episode">
                <Plus className="mr-1 h-4 w-4" /> Start episode
              </Button>
            </DialogTrigger>
            <NewEpisodeDialog
              profiles={profiles}
              onClose={() => setOpenNew(false)}
              onCreated={() => {
                setOpenNew(false);
                loadEpisodes();
              }}
            />
          </Dialog>
        </div>
      </div>

      {error && (
        <div className="rounded-lg border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive">
          {error}
        </div>
      )}

      {noProfiles ? (
        <NeedsProfile />
      ) : episodes.length === 0 ? (
        <div
          className="flex flex-col items-center justify-center rounded-3xl border-2 border-dashed border-border bg-card/50 p-16 text-center"
          data-testid="sick-empty"
        >
          <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-rose-100">
            <Heart className="h-7 w-7 text-rose-600" />
          </div>
          <h3 className="mt-4 font-heading text-xl font-bold">No illness episodes</h3>
          <p className="mt-2 max-w-sm text-sm text-muted-foreground">
            When someone gets sick, start an episode to log symptoms, temperatures, and doses.
          </p>
          <Button onClick={() => setOpenNew(true)} className="mt-6">
            <Plus className="mr-1 h-4 w-4" /> Start an episode
          </Button>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3" data-testid="sick-list">
          {episodes.map((ep) => {
            const profile = profiles.find((p) => p.id === ep.profile_id);
            return (
              <button
                key={ep.id}
                onClick={() => setActiveEpisode(ep)}
                data-testid={`sick-episode-card-${ep.id}`}
                className="group flex flex-col rounded-2xl bg-card p-6 text-left shadow-soft transition-shadow hover:shadow-card"
              >
                <div className="flex items-start justify-between gap-3">
                  <div className={`flex h-10 w-10 items-center justify-center rounded-xl ${ep.is_active ? "bg-rose-100 text-rose-600" : "bg-secondary text-primary"}`}>
                    <Activity className="h-5 w-5" />
                  </div>
                  <span className={`rounded-full px-2.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider ${ep.is_active ? "bg-rose-100 text-rose-700" : "bg-emerald-100 text-emerald-700"}`}>
                    {ep.is_active ? "active" : "resolved"}
                  </span>
                </div>
                <div className="mt-4 font-heading text-lg font-bold">{ep.title}</div>
                <div className="text-sm text-muted-foreground">
                  Started {ep.start_date}
                </div>
                {ep.notes && (
                  <div className="mt-3 line-clamp-2 text-xs text-muted-foreground">{ep.notes}</div>
                )}
                <div className="mt-4 border-t border-border pt-3 text-xs text-muted-foreground">
                  For {profile?.name || ep.profile_name || "—"} · Tap to view timeline →
                </div>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

function NewEpisodeDialog({ profiles, onClose, onCreated }) {
  const [form, setForm] = useState({
    profile_id: profiles?.[0]?.id || "",
    title: "",
    start_date: new Date().toISOString().slice(0, 10),
    notes: "",
  });
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    try {
      const { data: userData } = await supabase.auth.getUser();
      const user_id = userData?.user?.id;
      const profile = profiles.find((p) => p.id === form.profile_id);
      const { error: err } = await supabase.from("sick_episodes").insert({
        user_id,
        profile_id: form.profile_id,
        profile_name: profile?.name || null,
        title: form.title,
        start_date: form.start_date,
        notes: form.notes || null,
        is_active: true,
      });
      if (err) throw new Error(err.message);
      onCreated();
    } catch (err) {
      setError(err.message);
    }
  };

  return (
    <DialogContent className="max-w-md">
      <DialogHeader>
        <DialogTitle>Start illness episode</DialogTitle>
      </DialogHeader>
      <form onSubmit={submit} className="space-y-4">
        <div>
          <Label>For whom *</Label>
          <Select value={form.profile_id} onValueChange={(v) => setForm({ ...form, profile_id: v })}>
            <SelectTrigger data-testid="sick-episode-profile">
              <SelectValue placeholder="Select a profile" />
            </SelectTrigger>
            <SelectContent>
              {profiles.map((p) => (
                <SelectItem key={p.id} value={p.id}>
                  {p.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label>Title *</Label>
          <Input
            required
            value={form.title}
            onChange={(e) => setForm({ ...form, title: e.target.value })}
            placeholder="e.g. Stomach bug"
            data-testid="sick-episode-title"
          />
        </div>
        <div>
          <Label>Started *</Label>
          <Input
            type="date"
            required
            value={form.start_date}
            onChange={(e) => setForm({ ...form, start_date: e.target.value })}
            data-testid="sick-episode-started"
          />
        </div>
        <div>
          <Label>Notes</Label>
          <Textarea
            rows={3}
            value={form.notes}
            onChange={(e) => setForm({ ...form, notes: e.target.value })}
          />
        </div>
        {error && (
          <div className="rounded-lg border border-destructive/30 bg-destructive/10 px-3 py-2 text-xs text-destructive">
            {error}
          </div>
        )}
        <DialogFooter>
          <Button type="button" variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" data-testid="sick-episode-submit">Start episode</Button>
        </DialogFooter>
      </form>
    </DialogContent>
  );
}

function EpisodeDetail({ episode, profile, onBack }) {
  const [logs, setLogs] = useState([]);
  const [openAdd, setOpenAdd] = useState(false);

  const load = async () => {
    const { data } = await supabase
      .from("symptom_logs")
      .select("*")
      .eq("episode_id", episode.id)
      .order("logged_at", { ascending: false });
    setLogs(data || []);
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line
  }, [episode.id]);

  const groupedByDate = useMemo(() => {
    const groups = {};
    logs.forEach((e) => {
      const d = new Date(e.logged_at).toLocaleDateString();
      groups[d] = groups[d] || [];
      groups[d].push(e);
    });
    return groups;
  }, [logs]);

  const removeLog = async (id) => {
    await supabase.from("symptom_logs").delete().eq("id", id);
    load();
  };

  const toggleResolved = async () => {
    const newActive = !episode.is_active;
    await supabase
      .from("sick_episodes")
      .update({
        is_active: newActive,
        end_date: !newActive ? new Date().toISOString().slice(0, 10) : null,
      })
      .eq("id", episode.id);
    episode.is_active = newActive;
    // trigger UI rerender
    onBack();
  };

  return (
    <div className="mx-auto max-w-3xl space-y-6" data-testid="sick-episode-detail">
      <button
        onClick={onBack}
        className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
        data-testid="sick-back"
      >
        <ChevronLeft className="h-4 w-4" /> All episodes
      </button>

      <div className="rounded-3xl bg-card p-6 shadow-card sm:p-8">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <span className={`inline-block rounded-full px-3 py-0.5 text-[10px] font-semibold uppercase tracking-wider ${episode.is_active ? "bg-rose-100 text-rose-700" : "bg-emerald-100 text-emerald-700"}`}>
              {episode.is_active ? "active" : "resolved"}
            </span>
            <h1 className="mt-2 font-heading text-3xl font-extrabold">{episode.title}</h1>
            <p className="mt-1 text-sm text-muted-foreground">
              {profile?.name || episode.profile_name} · started {episode.start_date}
            </p>
            {episode.notes && (
              <p className="mt-3 max-w-xl text-sm text-muted-foreground">{episode.notes}</p>
            )}
          </div>
          <div className="flex flex-col gap-2 sm:items-end">
            <Dialog open={openAdd} onOpenChange={setOpenAdd}>
              <DialogTrigger asChild>
                <Button data-testid="sick-add-entry">
                  <Plus className="mr-1 h-4 w-4" /> Log entry
                </Button>
              </DialogTrigger>
              <AddLogDialog
                episodeId={episode.id}
                onClose={() => setOpenAdd(false)}
                onCreated={() => {
                  setOpenAdd(false);
                  load();
                }}
              />
            </Dialog>
            <AiSymptomButton episode={episode} profile={profile} logs={logs} />
            <Button variant="outline" onClick={toggleResolved} data-testid="sick-toggle-status">
              {episode.is_active ? "Mark resolved" : "Re-open"}
            </Button>
          </div>
        </div>
      </div>

      {logs.length === 0 ? (
        <div
          className="flex flex-col items-center justify-center rounded-2xl border-2 border-dashed border-border bg-card/50 p-12 text-center"
          data-testid="sick-entries-empty"
        >
          <ClipboardList className="h-10 w-10 text-muted-foreground" />
          <h3 className="mt-3 font-heading text-base font-bold">Empty timeline</h3>
          <p className="mt-1 max-w-xs text-sm text-muted-foreground">
            Log symptoms, temperatures, and meds to build a timeline you can share with the doctor.
          </p>
          <Button onClick={() => setOpenAdd(true)} className="mt-4">
            <Plus className="mr-1 h-4 w-4" /> Log first entry
          </Button>
        </div>
      ) : (
        <div className="space-y-6" data-testid="sick-timeline">
          {Object.entries(groupedByDate).map(([date, list]) => (
            <div key={date}>
              <div className="mb-3 font-heading text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                {date}
              </div>
              <ol className="relative space-y-3 border-l-2 border-border pl-4">
                {list.map((log) => (
                  <li key={log.id} className="group relative" data-testid={`sick-entry-${log.id}`}>
                    <span className="absolute -left-[1.45rem] flex h-6 w-6 items-center justify-center rounded-full bg-card ring-2 ring-border">
                      <Activity className="h-3 w-3 text-primary" />
                    </span>
                    <div className="rounded-xl bg-card p-4 shadow-soft">
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0 flex-1">
                          <div className="flex flex-wrap items-center gap-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                            {new Date(log.logged_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                            {log.severity && (
                              <span className={`rounded-full px-2 py-0.5 text-[9px] ${SEVERITY_COLOR[log.severity] || "bg-secondary text-foreground"}`}>
                                {log.severity}
                              </span>
                            )}
                          </div>

                          {log.temperature != null && (
                            <div className="mt-2 flex items-center gap-1.5 text-sm">
                              <Thermometer className="h-3.5 w-3.5 text-amber-600" />
                              <span className="font-heading text-base font-bold">
                                {log.temperature}°{log.temperature_unit || "F"}
                              </span>
                            </div>
                          )}

                          {log.symptoms?.length > 0 && (
                            <div className="mt-2 flex flex-wrap gap-1.5">
                              {log.symptoms.map((s) => (
                                <span key={s} className="rounded-full bg-rose-100 px-2.5 py-0.5 text-xs font-medium text-rose-700">
                                  {s}
                                </span>
                              ))}
                            </div>
                          )}

                          {log.medication_given && (
                            <div className="mt-2 flex items-center gap-1.5 text-xs text-muted-foreground">
                              <Pill className="h-3.5 w-3.5" />
                              {log.medication_given}
                              {log.medication_dose ? ` · ${log.medication_dose}` : ""}
                            </div>
                          )}

                          {log.fluid_intake && (
                            <div className="mt-1 flex items-center gap-1.5 text-xs text-muted-foreground">
                              <Droplet className="h-3.5 w-3.5" />
                              {log.fluid_intake}
                            </div>
                          )}

                          {log.notes && (
                            <div className="mt-2 flex items-start gap-1.5 text-xs text-muted-foreground">
                              <StickyNote className="mt-0.5 h-3.5 w-3.5" /> {log.notes}
                            </div>
                          )}
                        </div>

                        <button
                          onClick={() => removeLog(log.id)}
                          className="rounded p-1 text-muted-foreground opacity-0 hover:bg-destructive/10 hover:text-destructive group-hover:opacity-100"
                          data-testid={`sick-entry-delete-${log.id}`}
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    </div>
                  </li>
                ))}
              </ol>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function AddLogDialog({ episodeId, onClose, onCreated }) {
  const [form, setForm] = useState({
    logged_at: new Date().toISOString().slice(0, 16),
    symptoms: "",
    temperature: "",
    temperature_unit: "F",
    severity: "mild",
    medication_given: "",
    medication_dose: "",
    fluid_intake: "",
    notes: "",
  });
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    try {
      const { data: userData } = await supabase.auth.getUser();
      const user_id = userData?.user?.id;
      const payload = {
        user_id,
        episode_id: episodeId,
        logged_at: new Date(form.logged_at).toISOString(),
        symptoms: form.symptoms
          ? form.symptoms.split(",").map((s) => s.trim()).filter(Boolean)
          : [],
        temperature: form.temperature ? Number(form.temperature) : null,
        temperature_unit: form.temperature ? form.temperature_unit : null,
        severity: form.severity || null,
        medication_given: form.medication_given || null,
        medication_dose: form.medication_dose || null,
        fluid_intake: form.fluid_intake || null,
        notes: form.notes || null,
      };
      const { error: err } = await supabase.from("symptom_logs").insert(payload);
      if (err) throw new Error(err.message);
      onCreated();
    } catch (err) {
      setError(err.message);
    }
  };

  return (
    <DialogContent className="max-w-md">
      <DialogHeader>
        <DialogTitle>Log entry</DialogTitle>
      </DialogHeader>
      <form onSubmit={submit} className="space-y-4">
        <div>
          <Label>When *</Label>
          <Input
            type="datetime-local"
            value={form.logged_at}
            required
            onChange={(e) => setForm({ ...form, logged_at: e.target.value })}
            data-testid="entry-at"
          />
        </div>
        <div>
          <Label>Symptoms (comma separated)</Label>
          <Input
            value={form.symptoms}
            onChange={(e) => setForm({ ...form, symptoms: e.target.value })}
            placeholder="fever, sore throat, cough"
            data-testid="entry-symptoms"
          />
        </div>
        <div className="grid grid-cols-3 gap-2">
          <div className="col-span-2">
            <Label>Temperature</Label>
            <Input
              type="number"
              step="0.1"
              value={form.temperature}
              onChange={(e) => setForm({ ...form, temperature: e.target.value })}
              placeholder="101.4"
              data-testid="entry-temp"
            />
          </div>
          <div>
            <Label>Unit</Label>
            <Select value={form.temperature_unit} onValueChange={(v) => setForm({ ...form, temperature_unit: v })}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="F">°F</SelectItem>
                <SelectItem value="C">°C</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        <div>
          <Label>Severity</Label>
          <Select value={form.severity} onValueChange={(v) => setForm({ ...form, severity: v })}>
            <SelectTrigger data-testid="entry-severity">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="mild">Mild</SelectItem>
              <SelectItem value="moderate">Moderate</SelectItem>
              <SelectItem value="severe">Severe</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="grid grid-cols-2 gap-2">
          <div>
            <Label>Medication</Label>
            <Input
              value={form.medication_given}
              onChange={(e) => setForm({ ...form, medication_given: e.target.value })}
              placeholder="Ibuprofen"
              data-testid="entry-med"
            />
          </div>
          <div>
            <Label>Dose</Label>
            <Input
              value={form.medication_dose}
              onChange={(e) => setForm({ ...form, medication_dose: e.target.value })}
              placeholder="100mg"
            />
          </div>
        </div>
        <div>
          <Label>Fluids</Label>
          <Input
            value={form.fluid_intake}
            onChange={(e) => setForm({ ...form, fluid_intake: e.target.value })}
            placeholder="4 oz Pedialyte"
          />
        </div>
        <div>
          <Label>Notes</Label>
          <Textarea
            rows={2}
            value={form.notes}
            onChange={(e) => setForm({ ...form, notes: e.target.value })}
          />
        </div>
        {error && (
          <div className="rounded-lg border border-destructive/30 bg-destructive/10 px-3 py-2 text-xs text-destructive">
            {error}
          </div>
        )}
        <DialogFooter>
          <Button type="button" variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" data-testid="entry-submit">Log it</Button>
        </DialogFooter>
      </form>
    </DialogContent>
  );
}

function NeedsProfile() {  return (
    <div className="flex flex-col items-center justify-center rounded-3xl border-2 border-dashed border-border bg-card/50 p-16 text-center" data-testid="sick-needs-profile">
      <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-secondary">
        <Heart className="h-7 w-7 text-primary" />
      </div>
      <h3 className="mt-4 font-heading text-xl font-bold">Add a profile first</h3>
      <p className="mt-2 max-w-sm text-sm text-muted-foreground">
        Sick episodes are tracked per family member.
      </p>
      <Link to="/profiles">
        <Button className="mt-6">Go to profiles</Button>
      </Link>
    </div>
  );
}

// ============================================================================
// AI Symptom Checker
// ============================================================================
function AiSymptomButton({ episode, profile, logs }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <Button
        variant="outline"
        onClick={() => setOpen(true)}
        className="bg-accent/10 border-accent/30 text-accent-foreground hover:bg-accent/20"
        data-testid="sick-ai-check-btn"
      >
        <Sparkles className="mr-1 h-4 w-4" />
        AI symptom check
      </Button>
      {open && (
        <Dialog open={open} onOpenChange={setOpen}>
          <AiSymptomDialog
            episode={episode}
            profile={profile}
            logs={logs}
            onClose={() => setOpen(false)}
          />
        </Dialog>
      )}
    </>
  );
}

function AiSymptomDialog({ episode, profile, logs, onClose }) {
  const [loading, setLoading] = useState(true);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    const run = async () => {
      setLoading(true);
      setError("");
      try {
        // Aggregate the most recent 10 logs into a symptom snapshot
        const recent = (logs || []).slice(0, 10);
        const symptomSet = new Set();
        let latestTemp = null;
        let latestTempUnit = null;
        recent.forEach((log) => {
          (log.symptoms || []).forEach((s) => symptomSet.add(s));
          if (log.temperature != null && latestTemp == null) {
            latestTemp = log.temperature;
            latestTempUnit = log.temperature_unit;
          }
        });
        const symptoms = Array.from(symptomSet);

        // Compute age from profile.birthdate
        let ageYears = null;
        if (profile?.birthdate) {
          const bd = new Date(profile.birthdate);
          const diff = Date.now() - bd.getTime();
          ageYears = Math.max(0, Math.round((diff / (365.25 * 24 * 3600 * 1000)) * 10) / 10);
        }

        const resp = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/api/ai/symptom-check`,
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              profile_age_years: ageYears,
              symptoms: symptoms.length > 0 ? symptoms : [episode.title],
              temperature: latestTemp,
              temperature_unit: latestTempUnit,
              known_allergies: profile?.allergies || [],
              notes: episode.notes || null,
            }),
          }
        );
        if (!resp.ok) throw new Error(`AI service returned ${resp.status}`);
        const data = await resp.json();
        setResult(data);
      } catch (e) {
        setError(e.message || "AI service unavailable right now.");
      } finally {
        setLoading(false);
      }
    };
    run();
    // eslint-disable-next-line
  }, []);

  const urgencyStyles = {
    now: { label: "Call now / seek urgent care", color: "bg-destructive text-destructive-foreground" },
    today: { label: "Call your doctor today", color: "bg-amber-500 text-white" },
    in_24h: { label: "Contact within 24 hours", color: "bg-amber-100 text-amber-700" },
    monitor: { label: "Monitor at home", color: "bg-emerald-100 text-emerald-700" },
  };
  const urgency = result ? urgencyStyles[result.when_to_call_doctor] || urgencyStyles.monitor : null;

  return (
    <DialogContent className="max-w-2xl" data-testid="sick-ai-dialog">
      <DialogHeader>
        <DialogTitle className="flex items-center gap-2">
          <Sparkles className="h-5 w-5 text-accent" />
          AI symptom check for {profile?.name}
        </DialogTitle>
      </DialogHeader>
      {loading ? (
        <div className="flex items-center justify-center gap-3 py-10 text-muted-foreground" data-testid="sick-ai-loading">
          <div className="h-3 w-3 animate-pulse rounded-full bg-primary" />
          <span>Consulting gpt-5.2… this takes a few seconds.</span>
        </div>
      ) : error ? (
        <div className="rounded-xl border border-destructive/30 bg-destructive/10 px-4 py-3 text-sm text-destructive" data-testid="sick-ai-error">
          {error}
        </div>
      ) : result ? (
        <div className="space-y-5" data-testid="sick-ai-result">
          {urgency && (
            <div className={`rounded-2xl px-4 py-3 text-sm font-semibold ${urgency.color}`}>
              <span className="text-[10px] uppercase tracking-widest opacity-80">Recommended action</span>
              <div className="mt-1 font-heading text-lg font-bold">{urgency.label}</div>
            </div>
          )}
          <p className="text-sm leading-relaxed">{result.summary}</p>

          {result.red_flags?.length > 0 && (
            <div>
              <h4 className="font-heading text-sm font-bold text-destructive">🚨 Watch for these red flags</h4>
              <ul className="mt-2 space-y-1">
                {result.red_flags.map((r, i) => (
                  <li key={i} className="flex items-start gap-2 rounded-lg bg-destructive/5 px-3 py-1.5 text-sm">
                    <span className="mt-1 h-1.5 w-1.5 shrink-0 rounded-full bg-destructive" /> {r}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {result.likely_causes?.length > 0 && (
            <div>
              <h4 className="font-heading text-sm font-bold">Likely causes</h4>
              <ul className="mt-2 space-y-1 text-sm text-muted-foreground">
                {result.likely_causes.map((c, i) => (
                  <li key={i}>• {c}</li>
                ))}
              </ul>
            </div>
          )}

          {result.self_care?.length > 0 && (
            <div>
              <h4 className="font-heading text-sm font-bold">Self-care suggestions</h4>
              <ul className="mt-2 space-y-1 text-sm text-muted-foreground">
                {result.self_care.map((s, i) => (
                  <li key={i}>• {s}</li>
                ))}
              </ul>
            </div>
          )}

          <p className="rounded-lg bg-secondary/40 px-3 py-2 text-xs italic text-muted-foreground">
            {result.disclaimer}
          </p>
        </div>
      ) : null}
      <DialogFooter>
        <Button variant="ghost" onClick={onClose}>Close</Button>
      </DialogFooter>
    </DialogContent>
  );
}
